import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, IconButton, Tooltip, Typography, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import dayjs from 'dayjs';
import { useHistory, useRouteMatch } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import InvDataGrid from '../../components/InvDataGrid';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import ConfirmDialog from '../../components/ConfirmDialog';
import FilterToolbar from '../../components/FilterToolbar';
import { InvSelect, InvTextField } from '../../components/InvField';
import { getRequisitions, submitRequisition, approveRequisition, rejectRequisition,
  deleteRequisition, releaseRequisitionReservation } from '../../api/dispensing';
import { getStores } from '../../api/stockLedger';
import { formatDate } from '../../utils';
import { resolveDispensingScope, formatDispensingError } from '../../utils/dispensingEntity';
import { toInventoryPage } from '../../utils/masterEntity';
import { storeDisplayName, isRootStoreUser } from '../../utils/storeEntity';
import { IPD_COLORS, FILTER_TOOLBAR } from '../../theme';
import { PRIORITY_LEVELS, REQUISITION_STATUSES, APPROVAL_STATUSES } from '../../constants';
import useInvRole from '../../hooks/useInvRole';
import useAssignedStoreOptions from '../../hooks/useAssignedStoreOptions';
import { INV_ACTIONS } from '../../utils/inventoryRoles';

/**
 * Labeled Inv fields (IpdFormField) reserve a 22px helper-text row below the input;
 * unlabeled toolbar items (chips, static value, reset) need this offset to share
 * the same baseline in the flex-end toolbar rows.
 */
const FILTER_HELPER_OFFSET = '22px';

/** ConfirmDialog action keys on this screen → the permission each one needs. */
const DIALOG_ACTION_PERMISSION = {
  submit: INV_ACTIONS.REQUISITION_SUBMIT,
  approve: INV_ACTIONS.REQUISITION_APPROVE,
  reject: INV_ACTIONS.REQUISITION_REJECT,
  release: INV_ACTIONS.REQUISITION_RELEASE,
  cancel: INV_ACTIONS.REQUISITION_CANCEL,
};

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

const PRIORITY_COLORS = {
  EMERGENCY: IPD_COLORS.error.text, URGENT: IPD_COLORS.success.text,
  HIGH: IPD_COLORS.primary, NORMAL: IPD_COLORS.textSecondary, LOW: IPD_COLORS.textPrimary,
};

function todayDateStr() {
  return dayjs().format('YYYY-MM-DD');
}

function weekRangeDates() {
  return {
    from_date: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
    to_date: todayDateStr(),
  };
}

function normalizeList(data) {
  return Array.isArray(data) ? data : (data?.items || data?.data || []);
}

function StoreCell({ label }) {
  if (!label || label === '—') {
    return <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13 }}>—</Typography>;
  }
  return (
    <Tooltip title={label}>
      <Typography noWrap sx={{ color: IPD_COLORS.textPrimary, fontSize: 13 }}>
        {label}
      </Typography>
    </Tooltip>
  );
}

export default function RequisitionList() {
  const { url } = useRouteMatch();
  const history = useHistory();
  const { can } = useInvRole();
  const stockBase = url.replace('/requisitions', '');
  const defaultRangeRef = useRef(weekRangeDates());
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stores, setStores] = useState([]);
  const {
    storeLocked, activeStoreId, setActiveStoreId, storeOptions,
  } = useAssignedStoreOptions(stores, { allLabel: 'All Stores' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [approvalFilter, setApprovalFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [fromDate, setFromDate] = useState(defaultRangeRef.current.from_date);
  const [toDate, setToDate] = useState(defaultRangeRef.current.to_date);
  const [datePreset, setDatePreset] = useState('week');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState('');
  const [actioning, setActioning] = useState(false);
  const loadGenRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getStores()
      .then(d => { if (!cancelled) setStores(normalizeList(d)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(0);
  }, [statusFilter, approvalFilter, priorityFilter, fromDate, toDate]);

  const resolveStoreName = useCallback((storeId) => {
    if (!storeId) return '—';
    const store = stores.find(s => (s.store_id || s.id) === storeId);
    return store ? storeDisplayName(store) : storeId;
  }, [stores]);

  const lockedStoreLabel = storeOptions.find(o => String(o.value) === String(activeStoreId))?.label || '';
  const isParentStoreUser = isRootStoreUser(activeStoreId, stores);

  const load = useCallback(async () => {
    const gen = ++loadGenRef.current;
    if (fromDate && toDate && fromDate > toDate) {
      setError('From date must be on or before To date.');
      setItems([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const p = {
        limit: pageSize,
        offset: page * pageSize,
      };
      if (statusFilter) p.status = statusFilter;
      if (approvalFilter) p.approval_status = approvalFilter;
      if (priorityFilter) p.priority = priorityFilter;
      if (fromDate) p.from_date = fromDate;
      if (toDate) p.to_date = toDate;
      if (search) p.search = search;
      // The selected store scopes the list; empty = all stores. One param rather
      // than from_store_id/to_store_id: their store sits on both sides — they raise
      // requisitions and fulfil their sub-stores' — and the backend owns that semantic.
      if (activeStoreId) p.store_id = activeStoreId;
      // API: GET /inventory/dispensing/requisitions/
      const data = await getRequisitions(p);
      if (gen !== loadGenRef.current || !mountedRef.current) return;
      const { items: fetched, total } = toInventoryPage(data, { page, pageSize });
      setItems(fetched);
      setTotalCount(total);
    } catch (e) {
      if (gen !== loadGenRef.current || !mountedRef.current) return;
      setError(formatDispensingError(e, 'Failed to load requisitions'));
      setItems([]);
      setTotalCount(0);
    } finally {
      if (gen === loadGenRef.current && mountedRef.current) setLoading(false);
    }
  }, [statusFilter, approvalFilter, priorityFilter, fromDate, toDate, search, page, pageSize, activeStoreId]);

  useEffect(() => { load(); }, [load]);

  function applyTodayPreset() {
    const today = todayDateStr();
    setDatePreset('today');
    setFromDate(today);
    setToDate(today);
    setPage(0);
  }

  function applyWeekPreset() {
    const range = weekRangeDates();
    setDatePreset('week');
    setFromDate(range.from_date);
    setToDate(range.to_date);
    setPage(0);
  }

  function handleFromDateChange(value) {
    setDatePreset('custom');
    setFromDate(value);
  }

  function handleToDateChange(value) {
    setDatePreset('custom');
    setToDate(value);
  }

  async function executeAction() {
    const required = DIALOG_ACTION_PERMISSION[dialog.action];
    if (required && !can(required)) return;
    setActioning(true);
    try {
      const row = dialog.target;
      const id = row.transaction_id || row.id;
      const scope = resolveDispensingScope(row, stores);
      if (dialog.action === 'submit') await submitRequisition(id, scope);
      else if (dialog.action === 'approve') await approveRequisition(id, scope);
      else if (dialog.action === 'reject') await rejectRequisition(id, { ...scope, reason });
      else if (dialog.action === 'release') await releaseRequisitionReservation(id, scope);
      else if (dialog.action === 'cancel') await deleteRequisition(id, scope);
      setDialog(null);
      setReason('');
      load();
    } catch (e) {
      console.error('[RequisitionList] action failed:', e);
      setError(formatDispensingError(e, 'Action failed'));
    } finally {
      setActioning(false);
    }
  }

  const columns = useMemo(() => [
    { field: 'transaction_number', headerName: 'Req #', flex: 0.9, minWidth: 140 },
    {
      field: 'from_store_id',
      headerName: 'From Store',
      flex: 1.1,
      minWidth: 160,
      valueGetter: p => resolveStoreName(p.row.from_store_id),
      renderCell: p => <StoreCell label={p.value} />,
    },
    {
      field: 'to_store_id',
      headerName: 'To Store',
      flex: 1.1,
      minWidth: 160,
      valueGetter: p => resolveStoreName(p.row.to_store_id),
      renderCell: p => <StoreCell label={p.value} />,
    },
    {
      field: 'priority', headerName: 'Priority', flex: 0.6, minWidth: 100,
      renderCell: p => (
        <Typography sx={{ color: PRIORITY_COLORS[p.value] || IPD_COLORS.textSecondary, fontWeight: 600, fontSize: 12 }}>
          {p.value}
        </Typography>
      ),
    },
    { field: 'status', headerName: 'Status', flex: 0.7, minWidth: 120, renderCell: p => <StatusChip status={p.value} /> },
    {
      field: 'approval_status', headerName: 'Approval', flex: 0.7, minWidth: 120,
      renderCell: p => (p.value ? <StatusChip status={p.value} /> : null),
    },
    { field: 'transaction_date', headerName: 'Date', flex: 0.6, minWidth: 110, valueFormatter: p => formatDate(p.value) },
    {
      field: 'actions', headerName: '', flex: 0.5, minWidth: 140, sortable: false,
      renderCell: p => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View"><IconButton size="small" onClick={() => history.push(`${url}/${p.row.transaction_id || p.row.id}`)}
            sx={{ color: IPD_COLORS.primary }}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
          {p.row.status === 'DRAFT' && (
            <>
              {can(INV_ACTIONS.REQUISITION_EDIT) && (
                <Tooltip title="Edit"><IconButton size="small"
                  onClick={() => history.push(`${stockBase}/requisition/${p.row.transaction_id || p.row.id}/edit`)}
                  sx={{ color: IPD_COLORS.primary }}><EditIcon fontSize="small" /></IconButton></Tooltip>
              )}
              {can(INV_ACTIONS.REQUISITION_SUBMIT) && (
                <Tooltip title="Submit"><IconButton size="small"
                  onClick={() => setDialog({ action: 'submit', target: p.row, title: 'Submit Requisition', desc: 'Submit for approval?' })}
                  sx={{ color: IPD_COLORS.success.text }}><SendIcon fontSize="small" /></IconButton></Tooltip>
              )}
              {can(INV_ACTIONS.REQUISITION_CANCEL) && (
                <Tooltip title="Cancel"><IconButton size="small"
                  onClick={() => setDialog({ action: 'cancel', target: p.row, title: 'Cancel Requisition', desc: 'Cancel this draft requisition? It will be marked CANCELLED.', destructive: true })}
                  sx={{ color: IPD_COLORS.error.text }}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
              )}
            </>
          )}
          {p.row.status === 'SUBMITTED' && (
            <>
              {can(INV_ACTIONS.REQUISITION_APPROVE) && (
                <Tooltip title="Approve"><IconButton size="small"
                  onClick={() => setDialog({ action: 'approve', target: p.row, title: 'Approve Requisition', desc: 'Approve this requisition?' })}
                  sx={{ color: IPD_COLORS.success.text }}><CheckIcon fontSize="small" /></IconButton></Tooltip>
              )}
              {can(INV_ACTIONS.REQUISITION_REJECT) && (
                <Tooltip title="Reject"><IconButton size="small"
                  onClick={() => setDialog({ action: 'reject', target: p.row, title: 'Reject Requisition', desc: 'Reject this requisition?', destructive: true, requireReason: true })}
                  sx={{ color: IPD_COLORS.error.text }}><CloseIcon fontSize="small" /></IconButton></Tooltip>
              )}
            </>
          )}
          {p.row.status === 'RESERVED' && can(INV_ACTIONS.REQUISITION_RELEASE) && (
            <Tooltip title="Release Reservation"><IconButton size="small"
              onClick={() => setDialog({ action: 'release', target: p.row, title: 'Release Reservation', desc: 'Release the reserved stock for this requisition? Status returns to APPROVED.' })}
              sx={{ color: IPD_COLORS.warning.text }}><LockOpenIcon fontSize="small" /></IconButton></Tooltip>
          )}
        </Box>
      ),
    },
  ], [history, resolveStoreName, stockBase, url, can]);

  const todayActive = datePreset === 'today';
  const weekActive = datePreset === 'week';
  const defaultRange = defaultRangeRef.current;
  const hasActiveFilters = Boolean(
    statusFilter || approvalFilter || priorityFilter || searchInput.trim()
      || fromDate !== defaultRange.from_date || toDate !== defaultRange.to_date,
  );

  function resetFilters() {
    setStatusFilter('');
    setApprovalFilter('');
    setPriorityFilter('');
    setSearchInput('');
    setSearch('');
    applyWeekPreset();
  }

  return (
    <Box>
      <PageHeader
        eyebrow="Inventory Management"
        title="Requisitions"
        description={`${totalCount} requisition${totalCount === 1 ? '' : 's'} · newest first within selected dates`}
        actions={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
            {can(INV_ACTIONS.CONSOLIDATION_CREATE) && (
              <InvButton
                variant="outlined"
                tone="teal"
                startIcon={<MergeTypeIcon />}
                onClick={() => history.push(`${stockBase}/consolidations`)}
              >
                Consolidate
              </InvButton>
            )}
            {can(INV_ACTIONS.REQUISITION_CREATE) && !isParentStoreUser && (
              <InvButton startIcon={<AddIcon />} onClick={() => history.push(`${stockBase}/requisition/new`)}>
                New Requisition
              </InvButton>
            )}
          </Box>
        }
      />
      <FilterToolbar sx={{ mb: 0 }}>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '2 1 200px' }, minWidth: { xs: '100%', sm: 180 }, maxWidth: { xs: '100%', sm: 400 } }}>
          <InvTextField
            label="Search"
            value={searchInput}
            placeholder="Req # / remarks"
            onChange={e => setSearchInput(e.target.value)}
            helperText={
              searchInput.trim() && searchInput.trim() !== search
                ? 'Searching…'
                : undefined
            }
          />
        </Box>
        {storeLocked ? (
          <FilterToolbar.StaticValue label="Store" width={200} value={lockedStoreLabel} sx={{ mb: FILTER_HELPER_OFFSET }} />
        ) : (
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 180px' }, minWidth: { xs: '100%', sm: 170 }, maxWidth: { xs: '100%', sm: 300 } }}>
            <InvSelect
              label="Store"
              value={activeStoreId}
              onChange={e => setActiveStoreId(e.target.value)}
              options={storeOptions}
            />
          </Box>
        )}
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 160px' }, minWidth: { xs: '100%', sm: 150 }, maxWidth: { xs: '100%', sm: 260 } }}>
          <InvSelect
            label="Status"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            options={[{ value: '', label: 'All' }, ...REQUISITION_STATUSES.map(s => ({ value: s, label: s.replace(/_/g, ' ') }))]}
          />
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 160px' }, minWidth: { xs: '100%', sm: 150 }, maxWidth: { xs: '100%', sm: 260 } }}>
          <InvSelect
            label="Approval"
            value={approvalFilter}
            onChange={e => setApprovalFilter(e.target.value)}
            options={[{ value: '', label: 'All' }, ...APPROVAL_STATUSES.map(s => ({ value: s, label: s.replace(/_/g, ' ') }))]}
          />
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 150px' }, minWidth: { xs: '100%', sm: 130 }, maxWidth: { xs: '100%', sm: 240 } }}>
          <InvSelect
            label="Priority"
            value={priorityFilter}
            onChange={e => setPriorityFilter(e.target.value)}
            options={[{ value: '', label: 'All' }, ...PRIORITY_LEVELS.map(s => ({ value: s, label: s }))]}
          />
        </Box>
        {hasActiveFilters && (
          <Box sx={{ alignSelf: 'flex-end', mb: FILTER_HELPER_OFFSET }}>
            <FilterToolbar.Reset onClick={resetFilters} />
          </Box>
        )}
      </FilterToolbar>
      <FilterToolbar>
        <Box sx={{ flex: '0 0 auto', width: { xs: '100%', sm: 'auto' } }}>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.4, color: IPD_COLORS.textPrimary, mb: 0.75 }}>
            Quick Range
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, height: FILTER_TOOLBAR.controlHeight, alignItems: 'center' }}>
            <Chip
              label="Today"
              color={todayActive ? 'primary' : 'default'}
              variant={todayActive ? 'filled' : 'outlined'}
              onClick={applyTodayPreset}
              sx={{ height: FILTER_TOOLBAR.controlHeight, fontWeight: 600 }}
            />
            <Chip
              label="Last 7 days"
              color={weekActive ? 'primary' : 'default'}
              variant={weekActive ? 'filled' : 'outlined'}
              onClick={applyWeekPreset}
              sx={{ height: FILTER_TOOLBAR.controlHeight, fontWeight: 600 }}
            />
          </Box>
          <Box sx={{ height: FILTER_HELPER_OFFSET }} />
        </Box>
        <Box sx={{ flex: { xs: '1 1 45%', sm: '1 1 160px' }, minWidth: { xs: '45%', sm: 150 }, maxWidth: { xs: '100%', sm: 240 } }}>
          <InvTextField
            label="From Date"
            type="date"
            value={fromDate}
            InputLabelProps={{ shrink: true }}
            onChange={e => handleFromDateChange(e.target.value)}
            error={fromDate && toDate && fromDate > toDate ? 'From must be ≤ To' : null}
          />
        </Box>
        <Box sx={{ flex: { xs: '1 1 45%', sm: '1 1 160px' }, minWidth: { xs: '45%', sm: 150 }, maxWidth: { xs: '100%', sm: 240 } }}>
          <InvTextField
            label="To Date"
            type="date"
            value={toDate}
            InputLabelProps={{ shrink: true }}
            onChange={e => handleToDateChange(e.target.value)}
          />
        </Box>
      </FilterToolbar>
      <InvDataGrid
        fillHeight
        rows={items}
        columns={columns}
        loading={loading}
        error={error}
        onRetry={load}
        getRowId={r => r.transaction_id || r.id}
        emptyMessage="No requisitions found for the selected dates."
        emptyAction={can(INV_ACTIONS.REQUISITION_CREATE) && !isParentStoreUser
          ? <InvButton startIcon={<AddIcon />} onClick={() => history.push(`${stockBase}/requisition/new`)}>Create Requisition</InvButton>
          : null}
        paginationMode="server"
        rowCount={totalCount}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(model) => {
          if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
            setPage(0);
          } else {
            setPage(model.page);
          }
        }}
        pageSizeOptions={[10, 25, 50]}
      />
      <ConfirmDialog open={Boolean(dialog)} title={dialog?.title} description={dialog?.desc}
        destructive={dialog?.destructive} requireReason={dialog?.requireReason}
        reasonValue={reason} onReasonChange={setReason} loading={actioning}
        onConfirm={executeAction} onCancel={() => { setDialog(null); setReason(''); }} />
    </Box>
  );
}
